import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { DueLabel } from './TaskBits';
import {
	dueInfo,
	isSameDay,
	parseDateInput,
	quickDeadlineOptions,
	toDateInputValue,
} from './taskModel';

// Portal Work — set a task's due date from the LIST, without opening it.
//
// Anthony, 2026-08-13: "I want an easier way to add a due date instead of having
// to click Edit and scroll all the way down, find the date picker, and change
// the date. In Apple Reminders... when you click on the task, you can set the
// date right in that first state."
//
// So the due cell itself becomes the control. A dated row shows the same
// DueLabel it always did — now as a button; an UNDATED row previously rendered
// nothing at all in that cell, which is the actual reason there was no way in,
// and it now shows a quiet "Date" placeholder. Either one opens a small popover
// with the four presets people actually pick, a date field for anything else,
// and Clear.
//
// THREE THINGS THIS COMPONENT HAS TO GET RIGHT, ALL OF THEM BECAUSE OF WHERE IT
// LIVES:
//
// 1. THE WHOLE ROW IS A BUTTON. `.wt-row` carries role="button" + onClick →
//    onOpen and an Enter/Space keydown handler. Every pointer and key event in
//    here must stopPropagation or setting a date would ALSO open the detail —
//    i.e. the exact modal this feature exists to avoid. The row's own chevron
//    already established the pattern (TaskRow.js).
// 2. A PARKED TASK NEVER GETS THIS CONTROL. TaskRow swaps the due label for a
//    GateBadge when task.gate is set, and taskModel's dueBucket is explicit that
//    a gate outranks the deadline. Hanging a date setter off a gate badge would
//    invite writing a date onto work nobody intends to act on.
// 3. IT IS NOT WEB AWESOME, DELIBERATELY. This surface is bespoke `.wt-*` with
//    no wa-* anywhere in it, and a wa-dropdown here would import the boolean-prop
//    and disabled-is-not-a-guard traps into a control that must swallow its own
//    clicks. It follows SortMenu.js instead — the anchored popup in this same
//    folder that re-earns its ARIA by hand.
function DueQuickSet({ task, now, onSetDeadline, compact = false }) {
	const [open, setOpen] = useState(false);
	// Rows near the bottom of a long list would open the popover off-screen, so
	// it flips above the trigger when there isn't room below. Measured on open
	// rather than assumed from the row index — the list scrolls.
	const [dropUp, setDropUp] = useState(false);
	const wrapRef = useRef(null);
	const triggerRef = useRef(null);
	const popRef = useRef(null);
	const baseId = useId();

	const deadline = task?.deadline || null;
	const info = dueInfo(deadline, now);
	const options = quickDeadlineOptions(now);

	const close = useCallback((returnFocus = true) => {
		setOpen(false);
		if (returnFocus && triggerRef.current) triggerRef.current.focus();
	}, []);

	const commit = useCallback(
		(date) => {
			onSetDeadline?.(task, date);
			close();
		},
		[onSetDeadline, task, close]
	);

	// Close on any pointer press outside. pointerdown (not click) so it lands
	// before focus churn — same reasoning as SortMenu.
	useEffect(() => {
		if (!open) return undefined;
		const onDocDown = (e) => {
			if (wrapRef.current?.contains(e.target)) return;
			setOpen(false);
		};
		document.addEventListener('pointerdown', onDocDown);
		return () => document.removeEventListener('pointerdown', onDocDown);
	}, [open]);

	// On open, decide which way it opens, then move focus into it so the keyboard
	// path works and a screen reader is told where it landed.
	useEffect(() => {
		if (!open) return undefined;
		const rect = triggerRef.current?.getBoundingClientRect?.();
		const vh = typeof window === 'undefined' ? 0 : window.innerHeight || 0;
		// ~250px is the popover at its tallest (4 presets + date field + Clear).
		if (rect && vh) setDropUp(vh - rect.bottom < 250 && rect.top > 250);
		const raf = requestAnimationFrame(() => {
			popRef.current?.querySelector('button')?.focus();
		});
		return () => cancelAnimationFrame(raf);
	}, [open]);

	const stop = (e) => e.stopPropagation();

	// Escape is handled at the WRAPPER, not on the popover, so it works wherever
	// focus happens to be — the trigger keeps focus until the open effect's rAF
	// moves it, and an Escape in that window was doing nothing at all. The
	// stopPropagation is unconditional for the same reason every handler here
	// carries one: the row above listens for Enter/Space and would open the
	// detail underneath us.
	const onWrapKeyDown = (e) => {
		e.stopPropagation();
		if (open && e.key === 'Escape') {
			e.preventDefault();
			close();
		}
	};

	const onPopKeyDown = (e) => {
		if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
		e.preventDefault();
		const items = Array.from(
			popRef.current?.querySelectorAll('button, input') || []
		);
		if (!items.length) return;
		const i = items.indexOf(document.activeElement);
		const next = e.key === 'ArrowDown' ? i + 1 : i - 1;
		items[Math.min(items.length - 1, Math.max(0, next))]?.focus();
	};

	const onTriggerKeyDown = (e) => {
		if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
			e.preventDefault();
			setOpen(true);
		}
	};

	const label = info
		? `Due ${info.label} — change due date`
		: 'Set a due date';

	return (
		<div
			className={`wt-dueset${open ? ' is-open' : ''}`}
			ref={wrapRef}
			onClick={stop}
			onPointerDown={stop}
			onKeyDown={onWrapKeyDown}>
			<button
				ref={triggerRef}
				type="button"
				className={`wt-dueset__trigger${info ? '' : ' is-empty'}${
					compact ? ' is-compact' : ''
				}`}
				aria-haspopup="dialog"
				aria-expanded={open}
				aria-label={label}
				title={label}
				onClick={(e) => {
					e.stopPropagation();
					setOpen((v) => !v);
				}}
				onKeyDown={onTriggerKeyDown}>
				{info ? (
					<DueLabel task={task} now={now} />
				) : (
					<span className="wt-dueset__empty">
						<i className="fa-sharp fa-solid fa-calendar-plus" />
						<span className="wt-dueset__emptylabel">Date</span>
					</span>
				)}
			</button>

			{open && (
				<div
					ref={popRef}
					className={`wt-dueset__pop${dropUp ? ' is-up' : ''}`}
					role="dialog"
					aria-label="Set due date"
					id={`${baseId}-pop`}
					onKeyDown={onPopKeyDown}>
					<div className="wt-dueset__opts">
						{options.map((o) => (
							<button
								key={o.key}
								type="button"
								className={`wt-dueset__opt${
									isSameDay(deadline, o.date) ? ' is-current' : ''
								}`}
								onClick={(e) => {
									e.stopPropagation();
									commit(o.date);
								}}>
								<span className="wt-dueset__optlabel">{o.label}</span>
								<span className="wt-dueset__opthint">{o.hint}</span>
							</button>
						))}
					</div>

					<div className="wt-dueset__custom">
						<label
							className="wt-dueset__customlabel"
							htmlFor={`${baseId}-date`}>
							Pick a date
						</label>
						<input
							id={`${baseId}-date`}
							type="date"
							className="wt-dueset__input"
							value={toDateInputValue(deadline)}
							onClick={stop}
							onChange={(e) => {
								// parseDateInput, never `new Date(value)` — the raw string is
								// parsed as UTC midnight and lands a day early here.
								const d = parseDateInput(e.target.value);
								if (d) commit(d);
							}}
						/>
					</div>

					{deadline && (
						<button
							type="button"
							className="wt-dueset__clear"
							onClick={(e) => {
								e.stopPropagation();
								commit(null);
							}}>
							<i className="fa-sharp fa-solid fa-xmark" />
							Clear due date
						</button>
					)}
				</div>
			)}
		</div>
	);
}

export default DueQuickSet;
